import React, {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import {CoreEvent, Lang, ViewMode} from '@taopix/taopix-core';
import {useTaopix} from '../Context/TaopixContext';
import AppAuth from '../Authentication/AppAuth';
import {
  DisplayMode,
  LoadingOverlay,
  MatchesMediaQuery,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {AppMenuItem} from '../AppMenu/AppMenu';
import FlightCheckReviewDialog from '../Palette/FlightCheck/FlightCheckReviewDialog';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {DialogStatus, OpenMode} from '../../Enums';
import {useNavigate} from 'react-router-dom';
import NotificationManager from '../Dialogs/Notification/NotificationManager';
import {LocaleKey} from '@taopix/taopix-core/dist/types/types';
import useSettings from '../../Hooks/useSettings';
import ChangeProductDialog from '../ChangeProduct/ChangeProductDialog';
import {
  AlternativeProductData,
  useChangeProductContext,
} from '../Context/ChangeProduct/ChangeProductContext';
import {ChangeProductActions} from '../Context/ChangeProduct/ChangeProductActionTypes';
import UpgradeProductDialog from '../UpgradeProduct/UpgradeProductDialog';
import i18n from '../../i18n/i18n';
import AutoFillOptionDialog from '../AutoFill/AutoFillOptionDialog';
import ProjectNameDialog from '../Save/ProjectNameDialog';
import SavePrompt from '../Save/SavePrompt';
import OptionsAndExtrasWrapper from '../Wrapper/OptionsAndExtrasWrapper';
import {AssistantProvider} from '../Context/Assistant/AssistantContext';
import {ROUTE_ASSISTANT, ROUTE_PREVIEW} from '../../Constants';
import EditPictureDialog from '../Palette/Calendars/EditPictureDialog';
import {getUrl} from '../../Utils/Common';
import SmallScreenOrientationWarning from '../Mobile/SmallScreenOrientationWarning';
import {useSelector} from 'react-redux';
import {LayoutSelector} from '../../Selectors';
import EditorFactory from '../Editors/EditorFactory';
import InsideCoverNotEditableNotification from '../Tips/InsideCoverNotEditableTip';
import {useExperience} from '../Experience/ExperienceProvider';
import {useUserFeedback} from '../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../Context/UserFeedback/UserFeedbackActionTypes';
import LogoutConfirmationDialog from '../Logout/LogoutConfirmationDialog';

export type AppProps = {};

export type ControlCentreUser = {
  userName: string;
  userId: number;
  emailAddress: string;
  login: string;
  canCreateAccounts: boolean;
  isOrderSession: string;
  error: string;
};

// lazy load the lang dialog
const LanguageDialog = lazy(() => import('../Language/LanguageDialog'));

const Assistant = lazy(() => import('../Assistant/Assistant'));

// eslint-disable-next-line no-empty-pattern
const App = ({}: AppProps) => {
  // we need to load calendars because the layout palette requires it
  const {t} = useTranslation(['*', 'optionsandextras', 'calendars']);

  // get the app state
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const userFeedback = useUserFeedback();
  const navigate = useNavigate();
  const changeProduct = useChangeProductContext();
  const {experience} = useExperience();

  // get some props from the core
  const loader = window.Taopix.Core.get('loader');
  const server = app.core.get('server');

  const [showSavingOverlay, setShowSavingOverlay] = useState<boolean>(false);
  const productLayout = useSelector(LayoutSelector);

  /**
   * On change language, update the core strings
   */
  const onChangeLanguage = useCallback(
    (lang: string) => {
      // change the core language
      // if the language is not loaded set busy as it take a few secs to load in
      if (!i18n.languages.includes(lang)) {
        ui.dispatch(UserInterfaceActions.setIsBusy(true));
      }
      i18n.changeLanguage(lang).then(() => {
        localStorage.setItem('i18nextLng', lang);
        Lang.hydrate(server, lang as LocaleKey);

        setTimeout(() => {
          PubSub.publish(`${CoreEvent.RELOAD_PAGE}.${ui.state.pageNumber}`);
        }, 2000);
      });
    },
    [server, ui.state.pageNumber]
  );

  // After lang has been changed set busy to
  useEffect(() => {
    i18n.on('languageChanged', () => {
      ui.dispatch(UserInterfaceActions.setIsBusy(false));
    });
    return () => i18n.off('languageChanged', () => {});
  }, []);

  /**
   * Event to fire when the app busy state changes.
   */
  const onAppBusy = React.useCallback(event => {
    event.preventDefault();
  }, []);

  useLayoutEffect(() => {
    // Block keyboard key presses while the app is busy.
    // useLayoutEffect instead of useEffect as it binds the event faster.
    if (ui.state.busy) {
      window.document.addEventListener('keydown', onAppBusy);
    } else {
      window.document.removeEventListener('keydown', onAppBusy);
    }

    return () => {
      // Make sure to cleanup if the component unmounts.
      if (ui.state.busy) {
        window.document.removeEventListener('keydown', onAppBusy);
      }
    };
  }, [ui.state.busy]);

  const onClickItem = (value: AppMenuItem) => {
    switch (value) {
      case AppMenuItem.Language:
        ui.dispatch(UserInterfaceActions.setLanguageDialogOpen(true));
        break;
      case AppMenuItem.SignIn:
        userFlow.activateUserFlow(UserFlowType.Auth);
        userFlow.setCurrentStep(`${UserFlowStep.Auth}.${UserFlowStep.Login}`);
        break;
      case AppMenuItem.Register:
        userFlow.activateUserFlow(UserFlowType.Auth);
        userFlow.setCurrentStep(`${UserFlowStep.Auth}.${UserFlowStep.Register}`);
        break;
      case AppMenuItem.SignOut:
        userFlow.activateUserFlow(UserFlowType.Auth);
        userFlow.setCurrentStep(`${UserFlowStep.Auth}.${UserFlowStep.LogoutConfirmation}`);
        break;
      case AppMenuItem.About:
        window.open(getUrl(settings.state.urls.about), '_blank');
        break;
      case AppMenuItem.Help:
        window.open(getUrl(settings.state.urls.help), '_blank');
        break;
      case AppMenuItem.Share:
        userFlow.activateUserFlow(UserFlowType.Share);
        break;
      case AppMenuItem.Exit:
        window.location.href = getUrl(experience.SETTINGS.logoLinkUrl.logoLinkUrl);
        break;
      default:
    }
  };

  const smallScreen = MatchesMediaQuery('(max-width: 799px)');

  useEffect(() => {
    if (smallScreen) {
      ui.dispatch(UserInterfaceActions.setIsSmallScreen(true));
    } else {
      ui.dispatch(UserInterfaceActions.setIsSmallScreen(false));
    }
  }, [smallScreen]);

  useEffect(() => {
    // Switch the colour of the mobile browser surround to match the header or the work area (when in focus mode)
    const workarea = document.getElementById('workarea');
    const header = document.getElementById('editor-app-header');
    const workareaBg = workarea
      ? window.getComputedStyle(workarea).backgroundColor
      : 'rgb(255,255,255)';
    const headerBg = header ? window.getComputedStyle(header).backgroundColor : 'rgb(255,255,255)';
    const colour = ui.state.focusMode && ui.state.isSmallScreen ? workareaBg : headerBg;

    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', !app.state.loaded ? 'rgb(255,255,255)' : colour);
  }, [ui.state.focusMode, app.state.loaded]);

  // if the open mode is preview, redirect the user to the preview
  // This is to prevent the user from attempting to tamper with the /preview route
  useEffect(() => {
    if (OpenMode.Preview === settings.state?.behaviour?.openMode) {
      navigate(`/${ROUTE_PREVIEW}`);
    }
  }, [settings.state?.behaviour?.openMode]);

  // If the stage is assistant, redirect the user to the assistant
  // This is to prevent the user from attempting to tamper with the /assistant route
  useEffect(() => {
    if (!app.state.loaded) return;
    if (
      settings.state.project.stage === `#/${ROUTE_ASSISTANT}/` &&
      0 !== window.location.hash.indexOf(`#/${ROUTE_ASSISTANT}/`)
    ) {
      ui.dispatch(UserInterfaceActions.setIsBusy(true));
      loader.switch('assistant', settings.state.tokens.session).finally(() => {
        userFlow.activateUserFlow(UserFlowType.Assistant);
        navigate(`/${ROUTE_ASSISTANT}`);
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
      });
    }
  }, [app.state.loaded, settings.state.project.stage]);

  useEffect(() => {
    // change the project name if one is set
    if (ui.state.projectNaming.projectName !== '') {
      document.title = ui.state.projectNaming.projectName;
    }
  }, [ui.state.projectNaming.projectName]);

  useEffect(() => {
    const token = app.core.subscribe(
      CoreEvent.PROJECT_SAVING,
      (msg: string, data: {showSaveOverlay: boolean; saving: boolean}) => {
        // only show saving overlay if it is not an autoSave
        if (data.showSaveOverlay) {
          setShowSavingOverlay(data.saving);
        }
      }
    );

    return () => {
      app.core.unsubscribe(token);
    };
  }, []);

  useEffect(() => {
    if (app.state.loaded && ui.state.fromAssistant.fromAssistant) {
      userFlow.setExperience(experience);
      userFlow.activateUserFlow(UserFlowType.FromAssistant);
    }
  }, [app.state.loaded]);

  useEffect(() => {
    userFlow.setExperience(experience);
  }, [experience]);

  if (!app.state.loaded) {
    return (
      <LoadingOverlay
        id={'app-loading-overlay'}
        open={true}
        label={t('*:str_MessageStartingEditor')}
        theme={ThemeName.Container}
        overlayColour={'transparent'}
      />
    );
  }

  // set the core smart guide config
  const options = window.Taopix.Core.get('options');
  options.guides = settings.state.features.smartGuides;
  options.autoLayout = false;

  // The CSS classnames that define the view mode for use by the rest of the UI, not these are used in tailwind variants
  const viewModeClassNames = {
    [ViewMode.List]: 'editView listView',
    [ViewMode.Single]: 'editView pageView',
    [ViewMode.Grid]: 'gridView',
    [ViewMode.Preview]: 'previewView',
  };

  const appClasses = classNames(
    'flex',
    'flex-col',
    'flex-1',
    'max-w-full',
    'max-h-full',
    'min-h-0',

    // Turn focus mode on or off
    ui.state.focusMode && ui.state.isSmallScreen ? 'focus-mode-on' : 'focus-mode-off',

    // Add the view mode class name
    viewModeClassNames[ui.state.viewMode]
  );

  const onChangeProduct = (ref: number, isChangeProduct?: boolean) => {
    // close change product dialogs or set step if coming from upgrade product
    ui.dispatch(UserInterfaceActions.setIsBusy(true));
    if (isChangeProduct) {
      userFlow.setCurrentStep(`${UserFlowType.Change}.${UserFlowStep.None}`);
      changeProduct.dispatch(ChangeProductActions.setChangeProductDialogPreviewDialogOpen(false));
      changeProduct.dispatch(
        ChangeProductActions.setChangeProductDialogPreviewImageDialogOpen(false)
      );
    }

    app.core
      .get('stateManager')
      .call('changeProduct', {
        newLayout: ref,
        currentPage: ui.state.pageNumber,
        isChange: isChangeProduct,
        sessionRef: settings.state.tokens.session,
      })
      .catch(error => {
        let message = app.core.get('server').getErrorFromResponse(error);
        if ('' === message || !message) {
          message = error;
        }
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
        userFeedback.dispatch(
          UserFeedbackActions.setErrorUserFeedback({
            title: '',
            message,
            showHeader: true,
          })
        );
      });
    ui.dispatch(UserInterfaceActions.setHasProjectChanged(true));
    const changeProductModule = app.core.get('stateManager').getModule('changeProduct');
    changeProductModule?.on &&
      changeProductModule.on('onLoad', () => {
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
        userFlow.setCurrentStep(`${UserFlowType.Change}.${UserFlowStep.None}`);
        ui.dispatch(
          UserInterfaceActions.setDialogStatus({projectLayoutChangeNotification: DialogStatus.Open})
        );
      });
  };

  return (
    <>
      <LoadingOverlay
        id={'app-busy-overlay'}
        open={ui.state.busy}
        label={t('*:str_MessagePleaseWait')}
        delay={500}
        key={'app-busy-overlay'}
        theme={ThemeName.Popouts}
      />
      <Theme
        name={'tpx-theme-main'}
        allowBorder={false}
        allowCorners={false}
        allowTip={false}
        id="tpx-app"
        className={appClasses}
      >
        {/* Returns the relevant editor */}
        <EditorFactory onClickMenuItem={onClickItem} />

        {/* Assistant */}
        <Suspense
          fallback={
            <LoadingOverlay
              open={true}
              usePortal={true}
              label={t('*:str_MessageStartingAssistant')}
              theme={ThemeName.Popouts}
            />
          }
        >
          <AssistantProvider>
            <Assistant
              open={userFlow.isUserFlowActive(UserFlowType.Assistant)}
              displayMode={DisplayMode.Positioned}
            />
          </AssistantProvider>
        </Suspense>

        {/** Language Dialog */}
        <LanguageDialog isOpen={ui.state.languageDialogOpen} onChangeLanguage={onChangeLanguage} />

        <AutoFillOptionDialog
          open={ui.state.autoFillOptions.autoFillDialog}
          pageType={productLayout.retroprints ? 'print' : 'page'}
        />

        <FlightCheckReviewDialog
          isOpen={userFlow.isStepActive(UserFlowType.FlightCheck, UserFlowStep.FlightCheckReview)}
        />

        {/* AuthDialogs */}
        <AppAuth server={server} authentication={settings.state?.authentication} />

        <ChangeProductDialog
          open={userFlow.isStepActive(UserFlowType.Change, UserFlowStep.Change)}
          onChangeProduct={(ref: number) => onChangeProduct(ref, true)}
        />

        <UpgradeProductDialog
          open={
            userFlow.isStepActive(UserFlowType.Upgrade, UserFlowStep.Upgrade) &&
            changeProduct.state.canUpgradeProduct
          }
          product={changeProduct.state.upgradeProductData as AlternativeProductData}
          onChangeProduct={(ref: number) => onChangeProduct(ref, false)}
        />

        <ProjectNameDialog
          open={userFlow.isStepActive(UserFlowType.Save, UserFlowStep.ProjectName)}
        />

        {/* Options & Extras Dialog */}
        <OptionsAndExtrasWrapper server={server} />

        {/* Misc dialogs & overlays */}
        <NotificationManager />
        <SavePrompt open={ui.state.savePrompt} />
        <LoadingOverlay
          id={'app-saving-overlay'}
          open={showSavingOverlay}
          label={t('*:str_TitleSavingProject')}
          theme={ThemeName.Popouts}
        />
        <EditPictureDialog isOpen={ui.state.calendarEditPictureDialog} />
        <SmallScreenOrientationWarning />
        <InsideCoverNotEditableNotification
          open={ui.state.insideCoverNotEditableTip.open}
          title={ui.state.insideCoverNotEditableTip.title}
          message={ui.state.insideCoverNotEditableTip.message}
        />
        <LogoutConfirmationDialog
          open={userFlow.isStepActive(UserFlowType.Auth, UserFlowStep.LogoutConfirmation)}
        />
      </Theme>
    </>
  );
};

export default memo(App);
